import React from 'react';
import { styled, useTheme } from '@mui/material';
import { AreaChart as AreaChartRaw, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Area } from 'recharts';
import Dot from './Dot';
import { getSeries, getTicks } from '../../series';
import { AxisDomain, ChartData } from '../../ChartsTypes';
import { returnThemeSpacingAsNumber } from '../../../../utils/functions';
import { Tooltip } from 'recharts';
import TooltipBody from '../TooltipBody';

export interface AreaChartProps {
  type: 'areaChart';
  data: ChartData[];
  palette?: string[];
  tooltipValueFormatter?: (v: number) => string | number;
  yTicks?: AxisDomain;
}

const AreaChart: React.FC<AreaChartProps> = ({ data, palette, tooltipValueFormatter, yTicks }) => {
  const ticks = yTicks ? getTicks(yTicks) : [];
  const xAxisName = '__xAxis__';

  // Transforms the data to include the xAxisLabel as a key in the data object
  const chartData = data.map(({ values, xAxisLabel }) => ({
    ...values,
    [xAxisName]: xAxisLabel,
  }));
  const graphSeries = getSeries(chartData, palette);
  const theme = useTheme();

  return (
    <CustomResponsiveContainer>
      <AreaChartRaw
        margin={{
          bottom: returnThemeSpacingAsNumber(theme.spacing(2)),
          right: returnThemeSpacingAsNumber(theme.spacing(1.25)),
        }}
        data={data.map(({ values, xAxisLabel }) => ({
          ...values,
          [xAxisName]: xAxisLabel,
        }))}
      >
        {graphSeries.map(({ name, color }) => (
          <React.Fragment key={name}>
            <defs>
              <linearGradient id={`colorUv-${name}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="65%" stopColor={color} stopOpacity={0.1} />
                <stop offset="100%" stopColor={color} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <Area
              key={`${name} + ${color}`}
              type="monotone"
              dataKey={name}
              strokeWidth={3}
              dot={<Dot color={color} />}
              stroke={color}
              fill={`url(#colorUv-${name})`}
              fillOpacity={1}
            />
          </React.Fragment>
        ))}

        <CartesianGrid stroke="#F0F0F0" vertical={true} />

        <XAxis dataKey={xAxisName} axisLine={false} tickLine={false} style={{ fontSize: '10px' }} />
        <YAxis axisLine={false} tickLine={false} ticks={ticks} style={{ fontSize: '10px' }} domain={yTicks ? [yTicks.min, yTicks.max] : undefined} />

        <Tooltip
          wrapperStyle={{ outline: 'none' }}
          cursor={false}
          content={<TooltipBody valueFormatter={tooltipValueFormatter ? tooltipValueFormatter : (n) => n} series={graphSeries} />}
        />
      </AreaChartRaw>
    </CustomResponsiveContainer>
  );
};

export default AreaChart;

const CustomResponsiveContainer = styled(ResponsiveContainer)(({ theme }) => ({
  position: 'relative',
  maxWidth: '100%',
  marginLeft: `-${theme.spacing(2.5)}`,
  width: '100%',
  height: 300,
  minHeight: 300,
}));
